// Form components + their validators. Config types and getDefault* helpers
// are imported directly from each form module; the barrel re-exports only
// what call sites actually consume (plus getDefaultConfigForType below).
export { AdvisorImageForm, validateAdvisorImageConfig } from "./advisor-image-form";
export { AssetAllocationForm, validateAssetAllocationConfig } from "./asset-allocation-form";
export {
  AssetClassForecastForm,
  validateAssetClassForecastConfig,
} from "./asset-class-forecast-form";
export { ComplianceChartForm, validateComplianceChartConfig } from "./compliance-chart-form";
export { CostChartForm } from "./cost-chart-form";
export { CostTableForm } from "./cost-table-form";
export { ExpectedValueForm, validateExpectedValueConfig } from "./expected-value-form";
export {
  FinancialSituationForm,
  getDefaultFinancialSituationConfig,
} from "./financial-situation-form";
export type { GoalListConfig as PdfGoalListConfig } from "./goal-list-form";
export { GoalListForm } from "./goal-list-form";
export { HeadingForm, validateHeadingConfig } from "./heading-form";
export { HistoricalReturnForm } from "./historical-return-form";
export { ImageForm, validateImageConfig } from "./image-form";
export { PortfolioDisplayForm } from "./portfolio-display-form";
export { PortfolioForecastForm, validatePortfolioForecastConfig } from "./portfolio-forecast-form";
export { PortfolioXrayForm, validatePortfolioXrayConfig } from "./portfolio-xray-form";
export { ProductCostsForm } from "./product-costs-form";
export { RiskAssessmentForm, validateRiskAssessmentConfig } from "./risk-assessment-form";
export { SpacerForm, validateSpacerConfig } from "./spacer-form";
export { TableForm, validateTableConfig } from "./table-form";
export { TextForm, validateTextConfig } from "./text-form";
export { TitleOnlyForm } from "./title-only-form";
export * from "./types";

// Helper to get default config for any component type
import type { PdfSectionScope } from "@repo/shared/types";
import type { PdfComponentType } from "../types";
import { getDefaultAdvisorImageConfig } from "./advisor-image-form";
import { getDefaultAssetAllocationConfig } from "./asset-allocation-form";
import { getDefaultAssetClassForecastConfig } from "./asset-class-forecast-form";
import { getDefaultCostChartConfig } from "./cost-chart-form";
import { getDefaultExpectedValueConfig } from "./expected-value-form";
import { getDefaultFinancialSituationConfig } from "./financial-situation-form";
import { getDefaultHeadingConfig } from "./heading-form";
import { getDefaultHistoricalReturnConfig } from "./historical-return-form";
import { getDefaultImageConfig } from "./image-form";
import { getDefaultPortfolioDisplayConfig } from "./portfolio-display-form";
import { getDefaultPortfolioForecastConfig } from "./portfolio-forecast-form";
import { getDefaultPortfolioXrayConfig } from "./portfolio-xray-form";
import { getDefaultRiskAssessmentConfig } from "./risk-assessment-form";
import { getDefaultSpacerConfig } from "./spacer-form";
import { getDefaultTableConfig } from "./table-form";
import { getDefaultTextConfig } from "./text-form";

export function getDefaultConfigForType(
  type: PdfComponentType,
  /** QL-414 — where a new heading is being added, for its starting title. */
  heading?: { sectionScope?: PdfSectionScope; isFirstHeading?: boolean },
): Record<string, unknown> {
  switch (type) {
    case "text":
      return getDefaultTextConfig();
    case "heading":
      return getDefaultHeadingConfig(heading?.sectionScope, heading?.isFirstHeading);
    case "table":
      return getDefaultTableConfig();
    case "image":
      return getDefaultImageConfig();
    case "advisor_image":
      return getDefaultAdvisorImageConfig();
    case "spacer":
      return getDefaultSpacerConfig();
    case "portfolio_display":
      return getDefaultPortfolioDisplayConfig();
    case "portfolio_xray":
      return getDefaultPortfolioXrayConfig();
    case "risk_assessment":
      return getDefaultRiskAssessmentConfig();
    case "cost_chart":
      return getDefaultCostChartConfig();
    case "historical_return":
      return getDefaultHistoricalReturnConfig();
    case "asset_allocation":
      return getDefaultAssetAllocationConfig();
    case "asset_class_forecast":
      return getDefaultAssetClassForecastConfig();
    case "expected_value":
      return getDefaultExpectedValueConfig();
    case "portfolio_forecast":
      return getDefaultPortfolioForecastConfig();
    // Title-only components - each with specific defaults
    case "client_classification":
      return {
        title: "Client Classification",
        description: "Classification according to MiFID II requirements.",
      };
    case "sustainability_preferences":
      return {
        title: "Sustainability Preferences",
        description: "The client's stated sustainability preferences.",
      };
    case "table_of_contents":
      return { title: "Contents" };
    case "financial_situation":
      return getDefaultFinancialSituationConfig();
    case "client_preferences":
      return {
        title: "Client Preferences",
        description: "The client's answers to the preference questions.",
      };
    case "sustainability_status":
      return {
        title: "Sustainability Status",
        description: "How the portfolio's products match the sustainability preferences.",
      };
    case "volatility_chart":
      return {
        title: "Volatility Chart",
        description: "Portfolio volatility over time relative to the allowed limit.",
        timeRange: "10Y",
        showChart: true,
        showTable: true,
      };
    case "equity_share_chart":
      return {
        title: "Equity Share Chart",
        description: "The portfolio's equity share over time relative to the allowed limit.",
        timeRange: "10Y",
        showChart: true,
        showTable: true,
      };
    case "mandate":
      return {
        title: "Mandate",
        description: "",
      };
    case "mandate_comparison":
      return {
        title: "Mandate changes",
        description: "",
      };
    case "mandate_portfolio":
      return {
        title: "Mandate (portfolio)",
        description: "",
      };
    case "product_assessment":
      return {
        title: "Product Assessment",
        description: "Overview of investment experience and knowledge.",
      };
    case "quiz_results":
      return {
        title: "Knowledge Quiz Results",
        description: "Overview of the client's knowledge quiz results.",
      };
    case "cost_table":
      return {
        title: "Cost Summary",
        description: "Summary of all investment-related costs.",
      };
    case "portfolio_costs":
      return {
        title: "Advisor & Platform Fees",
        description: "Overview of advisory and platform costs.",
      };
    case "product_costs":
      return {
        title: "Product Costs",
        description: "Detailed breakdown of product-level costs.",
      };
    case "wealth_plan":
      return {
        title: "Wealth Plan",
        description: "Planned withdrawals and income schedule.",
      };
    case "goal_info":
      return {
        title: "Goal Information",
        description: "Key details about this investment goal.",
      };
    case "goal_list":
      return {
        title: "Goal List",
        description: "The goals covered by this advice.",
        showDescription: false,
      };
    case "trapets_lookup":
      return {
        title: "PEP/Sanction Check",
        description: "Results from PEP and sanctions screening.",
      };
    case "enin_lookup":
      return {
        title: "Company Registry Check",
        description: "Company risk flags from the ENIN registry lookup.",
      };
    case "sumsub_verification":
      return {
        title: "ID Verification",
        description: "Sumsub identity verification results.",
      };
    default:
      return {};
  }
}
